import type React from 'react';
import { cn } from '@/uptk/core/lib/utils';

export interface ErrorStateProps {
  /** Optional icon rendered in a destructive-tinted chip above the title */
  icon?: React.ReactNode;
  /** Primary, plain-language line (e.g. "Something went wrong") — already localized */
  title: string;
  /** Optional supporting sentence below the title — already localized */
  description?: string;
  /** Recovery-action label (e.g. "Try again") — already localized */
  retryLabel?: string;
  /** Recovery-action handler — renders the retry button when both label + handler are set */
  onRetry?: () => void;
  /**
   * Raw technical detail (e.g. `error.message` / `error.stack`).
   *
   * This is **never** shown to end users: it is rendered only in development
   * builds, inside a collapsed `<details>`, so engineers keep their diagnostics
   * while production users only ever see the keyed, plain-language copy above.
   * That is the whole point of i18n Rule 4a (UPT-37) — raw error text is not
   * user-facing copy.
   */
  detail?: string;
  /** Additional class names for the root */
  className?: string;
}

/**
 * Shared, tokenized error state (DS-0).
 *
 * One reusable surface for every failed-load / caught-error view so the copy is
 * keyed + plain-language and always offers a recovery path, instead of dumping a
 * raw `error.message`/`error.stack` at the user (i18n Rule 4a, UPT-37 / UPT-183).
 *
 * Built on the existing portal-* tokens and `ui-button` classes — no new design
 * tokens are introduced (mirrors the EmptyState shipped in UPT-131).
 */
export function ErrorState({
  icon,
  title,
  description,
  retryLabel,
  onRetry,
  detail,
  className,
}: Readonly<ErrorStateProps>) {
  const showRetry = !!(retryLabel && onRetry);
  // Raw error text is a developer diagnostic — never render it in production.
  const showDetail = !!detail && import.meta.env.DEV;

  return (
    <div
      role="alert"
      className={cn(
        'ui-error-state flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      {icon && (
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-theme-destructive/30 bg-theme-destructive/10 text-theme-destructive">
          {icon}
        </span>
      )}
      <p className="font-display text-lg font-semibold text-portal-ink">{title}</p>
      {description && (
        <p className="max-w-sm text-sm text-portal-ink-muted">{description}</p>
      )}
      {showRetry && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            className="ui-button ui-button-primary min-h-[44px]"
            onClick={onRetry}
          >
            {retryLabel}
          </button>
        </div>
      )}
      {showDetail && (
        // i18n-exempt: developer diagnostic, rendered only in DEV builds — not user-facing copy.
        <details className="mt-4 w-full max-w-xl text-left">
          <summary className="cursor-pointer text-xs font-medium text-portal-ink-muted">
            {
              // eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: dev-only diagnostic label, not user-facing copy
              'Technical details (dev only)'
            }
          </summary>
          <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-portal-surface-muted p-3 text-xs text-portal-ink-muted">
            {detail}
          </pre>
        </details>
      )}
    </div>
  );
}

export default ErrorState;
